import React, { createContext, useContext, useMemo } from 'react';

import { escapeRegExp, makeRegexOptions } from 'utils/stringHelpers';
import { IWithClassName } from '../common-props';

type HighlightableSpanContextModel = {
  search: string;
  highlightClass: string;
  highlightAll?: boolean;
};

type HighlightableSpanProps = IWithClassName & {
  /** The text on the highlightable span */
  text: string;
  /** If the search text to highlight is case sensitive */
  caseSensitive?: boolean;
};

export const HighlightableSpanContext = createContext<HighlightableSpanContextModel>({
  search: null,
  highlightClass: '',
  highlightAll: false,
});

export const HighlightableSpan: React.FC<HighlightableSpanProps> = ({
  text,
  caseSensitive = false,
  ...props
}) => {
  const { search, highlightClass, highlightAll } = useContext(HighlightableSpanContext);
  const regex = useMemo(
    () =>
      new RegExp(`(${escapeRegExp(search)})`, makeRegexOptions({ highlightAll, caseSensitive })),
    [search, highlightAll, caseSensitive]
  );
  const textWithHighlights = useMemo(
    () => (text || '').replace(regex, `<span class="${highlightClass}">$1</span>`),
    [text, regex, highlightClass]
  );

  return !search?.length ? (
    <span {...props}>{text}</span>
  ) : (
    <span dangerouslySetInnerHTML={{ __html: textWithHighlights }} {...props} />
  );
};
